import React, { useEffect, useState } from 'react';
import WeatherIcon from 'components/other/WeatherIcon'; // Assuming WeatherIcon is a custom component
import Navigation from 'components/other/Navigation'; // Assuming Navigation is a custom component
import DatePicker from 'react-datepicker'; // Assuming react-datepicker is used for date picking
import DailyThreshold from 'components/other/DailyThreshold';
import 'react-datepicker/dist/react-datepicker.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faTimes,
  faSave,
  faEdit,
  faTrashAlt,
  faPlus,
} from '@fortawesome/free-solid-svg-icons';
import { faCalendarAlt } from '@fortawesome/free-solid-svg-icons';
import {
  listTariffs,
  createTariff,
  updateTariff,
  deleteTariff,
} from 'services/gasTariffService';
import { GasConsumptionData, TariffData, TariffRecord } from 'types';
import { getGasConsumption } from 'services/gasConsumptionService';
import { getTotalCost } from 'services/totalCostService';
import { fromLocalDate } from 'utils/localTime';

type TariffRow = {
  editing: boolean;
  tariff: TariffRecord;
};

export function TariffRatesScreen() {
  const [tariffs, setTariffs] = useState<TariffRecord[]>([]);
  const [tariffsCount, setTariffsCount] = useState<number>(0);
  const [tariffRows, setTariffRows] = useState<TariffRow[]>([]);
  const [tariffRowsCount, setTariffRowsCount] = useState<number>(0);
  const [startDate, setStartDate] = useState<Date>(new Date());
  const [endDate, setEndDate] = useState<Date>(new Date());
  const [quantity, setQuantity] = useState<number>(12159);
  const [cost, setCost] = useState<number>(42159);

  useEffect(() => {
    const fetchTariffs = async () => {
      const tariffsData = await listTariffs();
      setTariffs(tariffsData);
    };

    fetchTariffs();
  }, [tariffsCount]);

  useEffect(() => {
    const fetchTariffRows = async () => {
      const newTariffRow = (tariff: TariffRecord) => {
        const newRow = {
          editing: false,
          tariff: {
            id: tariff.id,
            data: { ...tariff.data },
          },
        };
        tariffRows.forEach((oldRow) => {
          if (oldRow.editing && oldRow.tariff.id === tariff.id) {
            newRow.editing = true;
            newRow.tariff.data = { ...oldRow.tariff.data };
          }
        });
        return newRow;
      };

      const newTariffRows = tariffs.map(newTariffRow);

      tariffRows.forEach((oldRow) => {
        if (oldRow.editing && oldRow.tariff.id === '') {
          newTariffRows.push(oldRow);
        }
      });

      setTariffRows(newTariffRows);
    };

    fetchTariffRows();
  }, [tariffs, tariffRowsCount]);

  const updateTarrifs = () => setTariffsCount(tariffsCount + 1);

  const updateTarrifRows = () => setTariffRowsCount(tariffRowsCount + 1);

  const handleCalculate = async () => {
    const stDate = fromLocalDate(
      startDate.getFullYear(), startDate.getMonth(), startDate.getDate(),
      0, 0, 0
    );
    const enDate = fromLocalDate(
      endDate.getFullYear(), endDate.getMonth(), endDate.getDate() + 1,
      0, 0, 0
    );
    const { gas_consumption } =
      await getGasConsumption(stDate, enDate, 'h');
    const totalCost = (await getTotalCost(stDate, enDate)).total_cost;

    const qMCF = gas_consumption.reduce(
      (acc: number, { gas_consumption }: { gas_consumption: number }) =>
        acc + gas_consumption,
      0
    );
    setQuantity(qMCF);
    setCost(totalCost);
  };

  const handleTariffRateChange = (tariffRow: TariffRow, value: string) => {
    const v = parseFloat(value);
    if (isNaN(v)) {
      return;
    }
    tariffRow.tariff.data.tariff_rate = v;
    updateTarrifRows();
  };

  const handleTariffStartChange = (tariffRow: TariffRow, time: number) => {
    tariffRow.tariff.data.start_time_of_day = time;
    updateTarrifRows();
  };

  const handleTariffStopChange = (tariffRow: TariffRow, time: number) => {
    tariffRow.tariff.data.stop_time_of_day = time;
    updateTarrifRows();
  };

  const handleSaveTariff = async (tariffRow: TariffRow) => {
    console.log('tariffRow', tariffRow);
    if (tariffRow.tariff.id === '') {
      await createTariff(tariffRow.tariff.data);
    } else {
      await updateTariff(tariffRow.tariff.id, tariffRow.tariff.data);
    }
    tariffRow.editing = false;
    updateTarrifs();
  };

  const handleCancelTariff = (tariffRow: TariffRow) => {
    tariffRow.editing = false;
    updateTarrifRows();
  };

  const handleEditTariff = (tariffRow: TariffRow) => {
    tariffRow.editing = true;
    updateTarrifRows();
  };

  const handleDeleteTariff = async (tariffRow: TariffRow) => {
    await deleteTariff(tariffRow.tariff.id);
    updateTarrifs();
  };

  const handleAddTariff = () => {
    tariffRows.push({
      editing: true,
      tariff: {
        id: '',
        data: { start_time_of_day: 0, stop_time_of_day: 24, tariff_rate: 0 },
      },
    });
    updateTarrifRows();
  };

  return (
    <div className="screen new-screen overflow-hidden bg-background text-white">
      <div className="flex w-full justify-center">
        <div className="flex w-[90%] flex-col">
          <div className="container mx-auto mt-4 flex items-center justify-end space-x-2 px-20">
            <WeatherIcon /> {/* Custom component for weather icons */}
          </div>

          <div className="mt-6 grow px-20">
            <div className="mb-4 flex items-center justify-between text-2xl">
              <span>NATURAL GAS TARIFF RATES</span>
              <DailyThreshold />
            </div>
            <div className="bg-darkblue rounded-lg p-4">
              <div className="mb-4 flex items-center justify-center space-x-2">
                <div className="flex items-center space-x-2">
                  <FontAwesomeIcon icon={faCalendarAlt} className="text-xl" />
                  <DatePicker
                    selected={startDate}
                    onChange={(date: Date) => setStartDate(date)}
                    selectsStart
                    startDate={startDate}
                    endDate={endDate}
                    className="bg-lightblue rounded px-2 py-1 text-black"
                    dateFormat="dd/MM/yyyy"
                  />
                  <span>to</span>
                  <DatePicker
                    selected={endDate}
                    onChange={(date: Date) => setEndDate(date)}
                    selectsEnd
                    minDate={startDate}
                    startDate={startDate}
                    endDate={endDate}
                    className="bg-lightblue rounded px-2 py-1 text-black"
                    dateFormat="dd/MM/yyyy"
                  />
                </div>
                <button
                  onClick={handleCalculate}
                  className="btn rounded bg-blue-500 px-4 py-1 text-white"
                >
                  CALCULATE
                </button>
              </div>
              <div className="rounded-lg bg-white p-4 text-center text-black">
                <div className="text-xl">
                  QUANTITY BASED ON USER DEFINED DATE RANGES
                </div>
                <div className="my-2 text-3xl">
                  {quantity.toLocaleString()} MCF
                </div>
                <div className="text-3xl text-blue-500">
                  ${cost.toLocaleString()}
                </div>
              </div>
            </div>
          </div>

          <div className="mt-6 grow overflow-auto px-20">
            <div className="bg-darkblue rounded-lg p-4">
              <div className="mb-2 text-lg">Tariffs</div>

              {tariffRows.map((tariffRow, index) => (
                <div className="flex items-center space-x-2 py-2">
                  {tariffRow.editing ? (
                    <div key={index}>
                      <span>$</span>
                      <input
                        type="number"
                        min="0"
                        value={tariffRow.tariff.data.tariff_rate}
                        onChange={(e) =>
                          handleTariffRateChange(tariffRow, e.target.value)
                        }
                        className="bg-lightblue w-16 rounded px-2 py-1 text-center text-black"
                        placeholder="___"
                      />
                      <span> from </span>
                      <input
                        type="number"
                        min="0"
                        max="24"
                        value={tariffRow.tariff.data.start_time_of_day}
                        onChange={(e) =>
                          handleTariffStartChange(
                            tariffRow,
                            parseInt(e.target.value)
                          )
                        }
                        className="bg-lightblue w-16 rounded px-2 py-1 text-center text-black"
                        placeholder="___"
                      />
                      <span>h to </span>
                      <input
                        type="number"
                        min="0"
                        max="24"
                        value={tariffRow.tariff.data.stop_time_of_day}
                        onChange={(e) =>
                          handleTariffStopChange(
                            tariffRow,
                            parseInt(e.target.value)
                          )
                        }
                        className="bg-lightblue w-16 rounded px-2 py-1 text-center text-black"
                        placeholder="___"
                      />
                      <span>h </span>
                      <button
                        onClick={() => handleSaveTariff(tariffRow)}
                        className="ml-2 text-green-500"
                      >
                        <FontAwesomeIcon icon={faSave} />
                      </button>
                      <span> </span>
                      <button
                        onClick={() => handleCancelTariff(tariffRow)}
                        className="ml-2 text-red-500"
                      >
                        <FontAwesomeIcon icon={faTimes} />
                      </button>
                    </div>
                  ) : (
                    <div key={index}>
                      <span>$</span>
                      <input
                        type="number"
                        value={tariffRow.tariff.data.tariff_rate}
                        readOnly
                        className="bg-lightblue w-16 rounded px-2 py-1 text-center text-black"
                      />
                      <span> from </span>
                      <input
                        type="number"
                        value={tariffRow.tariff.data.start_time_of_day}
                        readOnly
                        className="bg-lightblue w-16 rounded px-2 py-1 text-center text-black"
                      />
                      <span>h to </span>
                      <input
                        type="number"
                        value={tariffRow.tariff.data.stop_time_of_day}
                        readOnly
                        className="bg-lightblue w-16 rounded px-2 py-1 text-center text-black"
                      />
                      <span>h </span>
                      <FontAwesomeIcon
                        icon={faEdit}
                        className="ml-auto cursor-pointer text-xl"
                        onClick={() => handleEditTariff(tariffRow)}
                      />
                      <span> </span>
                      <FontAwesomeIcon
                        icon={faTrashAlt}
                        className="cursor-pointer text-xl"
                        onClick={() => handleDeleteTariff(tariffRow)}
                      />
                    </div>
                  )}
                </div>
              ))}
              <button
                className="flex items-center space-x-2 py-2"
                onClick={handleAddTariff}
              >
                <FontAwesomeIcon
                  icon={faPlus}
                  className="cursor-pointer text-xl"
                />
                <span>Add new tariff</span>
              </button>
            </div>
          </div>

          <Navigation />
        </div>
      </div>
    </div>
  );
}
